<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">Mito-interpreter: ACMG/AMP-guided interpretation of mitochondrial DNA variants</h1>
      <div class="page-subtitle"><a href="https://cspec.genome.network/cspec/ui/svi/doc/GN015?version=1.0.0" target="_blank" class="title-link">ACMG/AMP Guidelines</a></div>
    </div>

    <div class="variant-input-section">
      <div class="input-row">
        <div class="input-group">
          <div class="input-label">Variant Position</div>
          <div class="input-wrapper">
            <input
              type="text"
              v-model="variantId"
              @input="fetchVariantData"
              placeholder="Enter Variant Position (e.g., 8993)"
              class="variant-input"
            />
          </div>
        </div>
        <div class="input-group">
          <div class="input-label">Reference</div>
          <div class="input-wrapper">
            <input
              type="text"
              v-model="reference"
              @input="fetchVariantData"
              placeholder="Reference"
              class="variant-input"
            />
          </div>
        </div>
        <div class="input-group">
          <div class="input-label">Alternate</div>
          <div class="input-wrapper">
            <input
              type="text"
              v-model="alternate"
              @input="fetchVariantData"
              placeholder="Alternate"
              class="variant-input"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="criteria-container">
      <div v-for="(block, blockIndex) in criteriaBlocks" :key="blockIndex" class="criteria-block">
        <div class="block-header">{{ block.title }}</div>
        <div class="block-items">
          <div 
            v-for="item in block.items" 
            :key="item.code"
            class="criteria-card"
            :class="getCriteriaClass(item.code)"
          >
            <div class="criteria-header">
              <span class="criteria-code" :class="getCodeClass(item.code)">{{ item.code }}</span>
              <span class="criteria-name">{{ item.name }}</span>
            </div>

            <div class="radio-group" v-if="item.code !== 'PM2' && item.code !== 'PP3' && item.code !== 'BP4'">
              <label 
                v-for="option in item.options" 
                :key="option.value"
                class="radio-option"
                :class="{ selected: item.selected === option.value, 'radio-preset': presetAppliedCodes.has(item.code) && item.selected === option.value }"
                @change="onManualChange(item.code)"
              >
                <input 
                  type="radio" 
                  :name="item.code" 
                  :value="option.value" 
                  v-model="item.selected"
                  class="radio-input"
                />
                <span class="radio-label">
                  <span class="asterisk" v-if="option.hasAsterisk">*</span>
                  {{ option.label }}
                </span>
                <span class="radio-caption" v-if="option.caption">{{ option.caption }}</span>
              </label>
            </div>

            <div v-if="item.code === 'PM2'">
              <div class="radio-group">
                <label 
                  v-for="option in item.options" 
                  :key="option.value"
                  class="radio-option"
                  :class="{ selected: item.selected === option.value, 'radio-preset': presetAppliedCodes.has(item.code) && item.selected === option.value }"
                  @change="onManualChange(item.code)"
                >
                  <input 
                    type="radio" 
                    :name="item.code" 
                    :value="option.value" 
                    v-model="item.selected"
                    class="radio-input"
                  />
                  <span class="radio-label">
                    <span class="asterisk" v-if="option.hasAsterisk">*</span>
                    {{ option.label }}
                  </span>
                  <span class="radio-caption" v-if="option.caption">{{ option.caption }}</span>
                </label>
              </div>
              <div class="reference-data">
                <div class="reference-title">Reference Data (Updated June 2026):</div>
                <template v-if="reference && alternate">
                  <div class="reference-item" v-for="(f, idx) in variantData.freq" :key="'freq-'+idx">
                    <span class="ref-badge">{{ f.resource }}: {{ f.allele }} Frequency = {{ f.freq }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.freq.length === 0">
                    <span class="ref-badge no-data">No frequency data found for {{ reference }}{{ variantId }}{{ alternate }}</span>
                  </div>
                </template>
                <template v-else>
                  <span class="ref-badge no-data">Enter Reference and Alternate to query frequency data</span>
                </template>
                <div class="reference-link">
                  <a href="https://gnomad.broadinstitute.org/" target="_blank" class="ext-link">gnomAD Database →</a>
                </div>
              </div>
            </div>

            <div v-if="item.code === 'PP3'">
              <div class="radio-group">
                <label 
                  v-for="option in item.options" 
                  :key="option.value"
                  class="radio-option"
                  :class="{ selected: item.selected === option.value, 'radio-preset': presetAppliedCodes.has(item.code) && item.selected === option.value }"
                  @change="onManualChange(item.code)"
                >
                  <input 
                    type="radio" 
                    :name="item.code" 
                    :value="option.value" 
                    v-model="item.selected"
                    class="radio-input"
                  />
                  <span class="radio-label">
                    <span class="asterisk" v-if="option.hasAsterisk">*</span>
                    {{ option.label }}
                  </span>
                  <span class="radio-caption" v-if="option.caption">{{ option.caption }}</span>
                </label>
              </div>
              <div class="reference-data">
                <div class="reference-title">nAPOGEE Score (tRNA; rRNA) [Updated June 2026]:</div>
                <template v-if="reference && alternate">
                  <div class="reference-item" v-for="(nap, idx) in variantData.napogee" :key="'nap-'+idx">
                    <span class="ref-badge">{{ nap.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.napogee.length === 0">
                    <span class="ref-badge no-data">No nAPOGEE data found for {{ reference }}{{ variantId }}{{ alternate }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="reference-item" v-for="(nap, idx) in variantData.napogee" :key="'nap-'+idx">
                    <span class="ref-badge">{{ nap.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.napogee.length === 0">
                    <span class="ref-badge no-data">No nAPOGEE data found</span>
                  </div>
                </template>
                <div class="score-divider"></div>
                <div class="reference-title">APOGEE Score (mRNA) [Updated June 2026]:</div>
                <template v-if="reference && alternate">
                  <div class="reference-item" v-for="(apo, idx) in variantData.apogee" :key="'apo-'+idx">
                    <span class="ref-badge">{{ apo.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.apogee.length === 0">
                    <span class="ref-badge no-data">No APOGEE data found for {{ reference }}{{ variantId }}{{ alternate }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="reference-item" v-for="(apo, idx) in variantData.apogee" :key="'apo-'+idx">
                    <span class="ref-badge">{{ apo.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.apogee.length === 0">
                    <span class="ref-badge no-data">No APOGEE data found</span>
                  </div>
                </template>
                <div class="scoring-table">
                  <div class="table-title">APOGEE Scoring:</div>
                  <table class="data-table">
                    <thead>
                      <tr><th>Score</th><th>Probability</th><th>Interpretation</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>≤ 0.062</td><td>≤ 0.001</td><td>Benign</td></tr>
                      <tr><td>0.062 - 0.265</td><td>0.001 - 0.1</td><td>Likely-benign</td></tr>
                      <tr><td>0.265 - 0.396</td><td>0.1 - 0.33</td><td>Low-scoring VUS</td></tr>
                      <tr><td>0.396 - 0.544</td><td>0.33 - 0.66</td><td>VUS</td></tr>
                      <tr><td>0.544 - 0.716</td><td>0.66 - 0.9</td><td>High-scoring VUS</td></tr>
                      <tr><td>0.716 - 0.907</td><td>0.9 - 0.99</td><td>Likely-pathogenic</td></tr>
                      <tr><td>≥ 0.907</td><td>≥ 0.99</td><td>Pathogenic</td></tr>
                    </tbody>
                  </table>
                </div>
                <div class="reference-link">
                  <a href="https://mitimpact.mcb2lab.org/" target="_blank" class="ext-link">For more information on APOGEE, please visit https://mitimpact.mcb2lab.org/</a>
                </div>
              </div>
            </div>

            <div v-if="item.code === 'BP4'">
              <div class="radio-group">
                <label 
                  v-for="option in item.options" 
                  :key="option.value"
                  class="radio-option"
                  :class="{ selected: item.selected === option.value, 'radio-preset': presetAppliedCodes.has(item.code) && item.selected === option.value }"
                  @change="onManualChange(item.code)"
                >
                  <input 
                    type="radio" 
                    :name="item.code" 
                    :value="option.value" 
                    v-model="item.selected"
                    class="radio-input"
                  />
                  <span class="radio-label">
                    <span class="asterisk" v-if="option.hasAsterisk">*</span>
                    {{ option.label }}
                  </span>
                  <span class="radio-caption" v-if="option.caption">{{ option.caption }}</span>
                </label>
              </div>
              <div class="reference-data">
                <div class="reference-title">nAPOGEE Score (tRNA; rRNA) [Updated June 2026]:</div>
                <template v-if="reference && alternate">
                  <div class="reference-item" v-for="(nap, idx) in variantData.napogee" :key="'nap2-'+idx">
                    <span class="ref-badge">{{ nap.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.napogee.length === 0">
                    <span class="ref-badge no-data">No nAPOGEE data found for {{ reference }}{{ variantId }}{{ alternate }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="reference-item" v-for="(nap, idx) in variantData.napogee" :key="'nap-'+idx">
                    <span class="ref-badge">{{ nap.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.napogee.length === 0">
                    <span class="ref-badge no-data">No nAPOGEE data found</span>
                  </div>
                </template>
                <div class="score-divider"></div>
                <div class="reference-title">APOGEE Score (mRNA) [Updated June 2026]:</div>
                <template v-if="reference && alternate">
                  <div class="reference-item" v-for="(apo, idx) in variantData.apogee" :key="'apo-'+idx">
                    <span class="ref-badge">{{ apo.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.apogee.length === 0">
                    <span class="ref-badge no-data">No APOGEE data found for {{ reference }}{{ variantId }}{{ alternate }}</span>
                  </div>
                </template>
                <template v-else>
                  <div class="reference-item" v-for="(apo, idx) in variantData.apogee" :key="'apo-'+idx">
                    <span class="ref-badge">{{ apo.result }}</span>
                  </div>
                  <div class="reference-item" v-if="variantData.apogee.length === 0">
                    <span class="ref-badge no-data">No APOGEE data found</span>
                  </div>
                </template>
                <div class="scoring-table">
                  <div class="table-title">APOGEE Scoring:</div>
                  <table class="data-table">
                    <thead>
                      <tr><th>Score</th><th>Probability</th><th>Interpretation</th></tr>
                    </thead>
                    <tbody>
                      <tr><td>≤ 0.062</td><td>≤ 0.001</td><td>Benign</td></tr>
                      <tr><td>0.062 - 0.265</td><td>0.001 - 0.1</td><td>Likely-benign</td></tr>
                      <tr><td>0.265 - 0.396</td><td>0.1 - 0.33</td><td>Low-scoring VUS</td></tr>
                      <tr><td>0.396 - 0.544</td><td>0.33 - 0.66</td><td>VUS</td></tr>
                      <tr><td>0.544 - 0.716</td><td>0.66 - 0.9</td><td>High-scoring VUS</td></tr>
                      <tr><td>0.716 - 0.907</td><td>0.9 - 0.99</td><td>Likely-pathogenic</td></tr>
                      <tr><td>≥ 0.907</td><td>≥ 0.99</td><td>Pathogenic</td></tr>
                    </tbody>
                  </table>
                </div>
                <div class="reference-link">
                  <a href="https://mitimpact.mcb2lab.org/" target="_blank" class="ext-link">For more information on APOGEE, please visit https://mitimpact.mcb2lab.org/</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="result-section" v-if="showResult">
      <div class="result-card" :class="resultClass">
        <div class="result-header">
          <div class="result-variant" v-if="presetDisplay || combinedAllele">
            <span v-if="presetDisplay">{{ presetDisplay }}</span>
            <span v-else-if="combinedAllele">{{ combinedAllele }}</span>
          </div>
          <div class="result-label">Predicted Result</div>
        </div>
        <div class="result-override-note" v-if="showOverrideNote">(ClinGen classification reflects expert panel override.)</div>
        <div class="result-body" :class="{ 'has-lookup': hasLookupMatch }">
          <div class="result-lookup" v-if="lookupData.clingen">
            <div class="lookup-item">
              <span class="lookup-source">ClinGen</span>
              <span class="lookup-status">{{ lookupData.clingen }}</span>
            </div>
          </div>
          <div class="result-divider" v-if="lookupData.clingen"></div>
          <div class="result-main">
            <div class="lookup-source">ACMG/AMP</div>
            <div class="result-value">{{ result }}</div>
          </div>
          <div class="result-divider" v-if="lookupData.mitomap"></div>
          <div class="result-lookup" v-if="lookupData.mitomap">
            <div class="lookup-item">
              <span class="lookup-source">MITOMAP</span>
              <span class="lookup-status">{{ lookupData.mitomap }}</span>
            </div>
          </div>
        </div>
        <div class="result-footnote">The reference data was updated in June 2026.</div>
      </div>

      <div class="info-card">
        <div class="info-card-title">NOTE</div>
        <p>For some classification results, specific letters that represent ClinGen pathogenicity ratings (assigned following curation by the VCEP, the mtDNA Variant Curation Expert Panel) are shown in brackets in the Mitomap Status column. The following abbreviations are used: B, Benign; LB, Likely Benign; VUS, Variant of Uncertain Significance; LP, Likely Pathogenic; P Pathogenic.</p>
        <p>Note that the ClinGen scoring is quite stringent and gives fewer points than Mitomap does for many types of evidence, e.g., cybrid & other functional studies, in-silico tools, absence in large databases, heteroplasmy, de-novo requirements, and case numbers.</p>
      </div>

      <div class="selection-summary">
        <div class="summary-title">Selection Summary</div>
        <div class="selection-tables-container">
          <table class="selection-table" v-for="(chunk, chunkIdx) in summaryChunks" :key="chunkIdx">
            <thead>
              <tr>
                <th>Criteria</th>
                <th>Selection</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in chunk" :key="item.code" :class="[getSummaryRowClass(item), { 'summary-preset': presetAppliedCodes.has(item.code) }]">
                <td class="summary-code">{{ item.code }}</td>
                <td class="summary-selection">{{ item.selected }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="presetAppliedCodes.size > 0" class="preset-disclaimer">
          Entries with a gold-colored background were populated using the ClinGen database.
          <a v-if="presetLink" :href="presetLink" target="_blank" class="preset-link">View source classification &rarr;</a>
        </div>
      </div>
    </div>

    <div class="action-section">
      <button class="predict-btn" @click="predict">
        <span class="btn-icon">🧪</span>
        <span class="btn-text">Click to Predict</span>
      </button>
    </div>

    <div class="citation-footer">
      <p class="citation-text">
        Citation: Zhang N, et al. Mito-interpreter: An online tool for implementing the ACMG/AMP standards and guidelines for the<br>interpretation of mitochondrial DNA variants. Mitochondrion. 2026;102194. doi: 10.1016/j.mito.2026.102194. <a href="https://pubmed.ncbi.nlm.nih.gov/42486175/" target="_blank" class="citation-link">PMID: 42486175</a>.
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'ACMGAssessment',
  data() {
    return {
      variantId: 8993,
      reference: 'T',
      alternate: 'G',
      presetDisplay: '',
      showResult: false,
      presetAppliedCodes: new Set(),
      presetLink: '',
      result: '',
      lookupData: { clingen: null, mitomap: null },
      variantData: {
        reference: [],
        exact_reference: [],
        mitotip: [],
        apogee: [],
        napogee: [],
        freq: []
      },
      allCriteria: [
        {
          code: 'PVS1',
          name: 'PVS1 (mRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Very Strong', label: 'Very Strong', caption: 'Large heteroplasmic mtDNA deletions, where at least one gene is completely deleted' },
            { value: 'Strong', label: 'Strong', caption: 'Assessment of small deletions, nonsense, and frameshift variants in protein-coding genes should follow established guidelines (Abou Tayoun et al., 2018)' },
            { value: 'Moderate', label: 'Moderate', caption: 'Assessment of small deletions, nonsense, and frameshift variants in protein-coding genes should follow established guidelines (Abou Tayoun et al., 2018)' },
            { value: 'Supporting', label: 'Supporting', caption: 'Assessment of small deletions, nonsense, and frameshift variants in protein-coding genes should follow established guidelines (Abou Tayoun et al., 2018)' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PS1',
          name: 'PS1 (mRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Strong', label: 'Strong', caption: 'Same amino acid change as a previously established pathogenic variant regardless of nucleotide change' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PS2',
          name: 'PS2 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Very Strong', label: 'Very Strong', caption: 'De novo (maternity confirmed or identical full mtDNA sequence) in a patient with the disease and no family history; with weighting per ClinGen SVI guidance' },
            { value: 'Strong', label: 'Strong', caption: 'De novo (maternity confirmed or identical full mtDNA sequence) in a patient with the disease and no family history; with weighting per ClinGen SVI guidance' },
            { value: 'Moderate', label: 'Moderate', caption: 'De novo (maternity confirmed or identical full mtDNA sequence) in a patient with the disease and no family history; with weighting per ClinGen SVI guidance' },
            { value: 'Supporting', label: 'Supporting', caption: 'De novo (maternity confirmed or identical full mtDNA sequence) in a patient with the disease and no family history; with weighting per ClinGen SVI guidance' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PS3',
          name: 'PS3 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Moderate', label: 'Moderate', caption: 'This category is not incorporated within the ACMG/AMP variant assessment criteria, yet it is adopted during variant evaluation workflows established by the ClinGen mtDNA Variant Curation Expert Panel.', hasAsterisk: true },
            { value: 'Supporting', label: 'Supporting', caption: 'Functional validation is present in cybrid studies or single fiber analysis' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PS4',
          name: 'PS4 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Strong', label: 'Strong', caption: 'Variant present in ≥16 unrelated probands' },
            { value: 'Moderate', label: 'Moderate', caption: 'Variant present in ≥4 unrelated probands' },
            { value: 'Supporting', label: 'Supporting', caption: 'Variant present in 2 unrelated probands in different top-level haplogroups' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PM2',
          name: 'PM2 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: 'Frequency <0.00002 (0.002%, 1/50,000) from controls' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PM4',
          name: 'PM4 (mRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Moderate', label: 'Moderate', caption: 'Protein length changes due to in-frame deletions/insertions in a non-repeat region or stop-loss variants' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PM5',
          name: 'PM5 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Moderate', label: 'Moderate', caption: 'Novel missense change at an amino acid residue where a different missense change determined to be pathogenic has been seen before. (protein-coding genes)' },
            { value: 'Supporting', label: 'Supporting', caption: 'Same nucleotide position as previously established pathogenic variant in a rRNA/tRNA' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PM6',
          name: 'PM6 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Very Strong', label: 'Very Strong', caption: 'Assumed de novo, but without confirmation of maternity (maternal testing done by targeted variant analysis and/or targeted gene sequencing)' },
            { value: 'Strong', label: 'Strong', caption: 'Assumed de novo, but without confirmation of maternity (maternal testing done by targeted variant analysis and/or targeted gene sequencing)' },
            { value: 'Moderate', label: 'Moderate', caption: 'Assumed de novo, but without confirmation of maternity (maternal testing done by targeted variant analysis and/or targeted gene sequencing)' },
            { value: 'Supporting', label: 'Supporting', caption: 'Assumed de novo, but without confirmation of maternity (maternal testing done by targeted variant analysis and/or targeted gene sequencing)' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PP1',
          name: 'PP1 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Moderate', label: 'Moderate', caption: 'Co-segregation with disease in 5+ maternal family members and level of heteroplasmy segregating with disease manifestations' },
            { value: 'Supporting', label: 'Supporting', caption: 'Co-segregation with disease in 2-4 maternal family members and level of heteroplasmy segregating with disease manifestations' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PP3',
          name: 'PP3 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: 'Multiple lines of computational evidence support a deleterious effect on the gene or gene product (conservation, evolutionary, etc)' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'PP4',
          name: 'PP4 (mRNA; tRNA; rRNA)',
          type: 'pathogenic',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: 'Decreased ETC enzyme activity (<20%) performed in a CLIA-approved (or equivalently-certified) laboratory in muscle, liver, and/or fibroblasts (for fibroblasts, must be seen in multiple unrelated probands and/or assayed in different individuals).' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BA1',
          name: 'BA1 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Stand Alone', label: 'Stand Alone', caption: 'Top-level haplogroup defining variants in individuals that are members of that same top-level haplogroup OR Allele frequency > 0.01 (1%)' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BS1',
          name: 'BS1 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Strong', label: 'Strong', caption: 'Allele frequency 0.005 - 0.0099 (0.5% - 0.99%)' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BS2',
          name: 'BS2 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Strong', label: 'Strong', caption: 'Observed at a higher heteroplasmy in a healthy adult individual, especially in healthy maternal family members, than in same tissue tested in an affected individual' },
            { value: 'Supporting', label: 'Supporting', caption: 'Observed at a higher heteroplasmy in a healthy adult individual, especially in healthy maternal family members, than in different tissue(s) tested in an affected individual' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BS3',
          name: 'BS3 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: 'No evidence of functional effect in cybrid studies or single fiber analysis is present (no statistically significant difference from control; mean values of <2 SD from control mean, or 50% enzyme activity compared to controls).' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BS4',
          name: 'BS4 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Strong', label: 'Strong', caption: 'Lack of segregation in affected members of a family and/or segregation of disease in paternal family members' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BP2',
          name: 'BP2 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: "Other mtDNA variant is observed in individual's mtDNA that has previously been confirmed to be pathogenic" },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BP4',
          name: 'BP4 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: 'Multiple lines of computational evidence suggest no impact on gene or gene product (conservation, evolutionary, etc)' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BP5',
          name: 'BP5 (mRNA; tRNA; rRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: 'Mitochondrial DNA variant found in a case with a nuclear DNA-related disease' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        },
        {
          code: 'BP7',
          name: 'BP7 (mRNA)',
          type: 'benign',
          selected: 'Not Applicable',
          options: [
            { value: 'Supporting', label: 'Supporting', caption: 'A synonymous (silent) variant.' },
            { value: 'Not Applicable', label: 'Not Applicable' }
          ]
        }
      ]
    }
  },
  computed: {
    combinedAllele() {
      if (this.variantId && this.alternate) {
        return `${this.reference}${this.variantId}${this.alternate}`
      }
      return ''
    },
    hasLookupMatch() {
      return this.lookupData.clingen !== null || this.lookupData.mitomap !== null
    },
    criteriaBlocks() {
      const blockDefinitions = [
        { title: 'Null variant', codes: ['PVS1'] },
        { title: 'Nucleotide and/or amino acid position', codes: ['PS1', 'PM5', 'BP7'] },
        { title: 'De novo variants', codes: ['PS2', 'PM6'] },
        { title: 'Functional studies', codes: ['PS3', 'BS3'] },
        { title: 'Variant frequency', codes: ['PM2', 'BA1', 'BS1'] },
        { title: 'Protein length changes', codes: ['PM4'] },
        { title: 'Segregation and presence in unrelated probands', codes: ['PP1', 'PS4', 'BS2', 'BS4'] },
        { title: 'Computational evidence', codes: ['PP3', 'BP4'] },
        { title: 'Phenotype', codes: ['PP4'] },
        { title: 'Haplogroup-defining variants', codes: ['BA1'] },
        { title: 'Alternate disease cause', codes: ['BP2', 'BP5'] }
      ]

      return blockDefinitions.map(block => ({
        title: block.title,
        items: block.codes.map(code => this.allCriteria.find(c => c.code === code)).filter(Boolean)
      })).filter(block => block.items.length > 0)
    },
    resultClass() {
      if (this.result === 'P') return 'result-pathogenic'
      if (this.result === 'LP') return 'result-likely-pathogenic'
      if (this.result === 'B') return 'result-benign'
      if (this.result === 'LB') return 'result-likely-benign'
      return 'result-vus'
    },
    resultDescription() {
      if (this.result === 'P') return 'Pathogenic - The variant is classified as pathogenic'
      if (this.result === 'LP') return 'Likely Pathogenic - The variant is classified as likely pathogenic'
      if (this.result === 'B') return 'Benign - The variant is classified as benign'
      if (this.result === 'LB') return 'Likely Benign - The variant is classified as likely benign'
      return 'Variant of Uncertain Significance (VUS)'
    },
    showOverrideNote() {
      const overridePositions = [
        '12425del', '5814T>C', '5703G>A', '9191T>C', '14597A>G',
        '8313G>A', '7471dup', '14495A>G', '1555A>G', '9205_9206del',
        '5690A>G', '7445A>G', '5556G>A', '591C>T', '15150G>A',
        '7896G>A', '10663T>C', '11778G>A', '8344A>G', '15990C>T',
        '7511T>C', '3571del'
      ]
      return overridePositions.includes(this.presetDisplay)
    },
    summaryChunks() {
      const chunks = []
      const chunkSize = Math.ceil(this.allCriteria.length / 2)
      for (let i = 0; i < this.allCriteria.length; i += chunkSize) {
        chunks.push(this.allCriteria.slice(i, i + chunkSize))
      }
      return chunks
    }
  },
  props: {
    preset: {
      type: Object,
      default: null
    }
  },
  watch: {
    preset: {
      handler(newPreset) {
        if (newPreset) {
          this.applyPreset(newPreset)
        }
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.fetchVariantData()
  },
  methods: {
    applyPreset(preset) {
      // 重置所有选项为Not Applicable
      this.allCriteria.forEach(criteria => {
        criteria.selected = 'Not Applicable'
      })
      
      // 设置预设显示名称
      this.presetDisplay = preset.position || ''
      
      // 先清空reference和alternate
      this.variantId = ''
      this.reference = ''
      this.alternate = ''
      
      // 设置variantId, reference, alternate
      if (preset.variant_id) {
        this.variantId = preset.variant_id
      }
      if (preset.ref) {
        this.reference = preset.ref
      }
      if (preset.alt) {
        this.alternate = preset.alt
      }
      
      // 处理met_codes
      preset.met_codes.forEach(codeStr => {
        this.applyCode(codeStr, true)
      })
      
      // 处理not_met_codes
      preset.not_met_codes.forEach(codeStr => {
        this.applyCode(codeStr, false)
      })

      // 记录预设填充的条目
      this.presetAppliedCodes = new Set(preset.met_codes.map(codeStr => {
        return codeStr.includes('_') ? codeStr.split('_')[0] : codeStr
      }))

      // 记录预设链接
      this.presetLink = preset.link || ''

      // 重新获取variant数据
      this.$nextTick(() => {
        this.fetchVariantData()
      })
    },
    applyCode(codeStr, isMet) {
      // 解析codeStr，格式类似"PM2_Supporting"或"PS1"
      let code = codeStr
      let level = null
      
      // 检查是否有后缀
      if (codeStr.includes('_')) {
        const parts = codeStr.split('_')
        code = parts[0]
        const suffix = parts.slice(1).join('_')
        if (suffix) {
          if (suffix.includes('Stand')) level = 'Stand Alone'
          else if (suffix.includes('Very')) level = 'Very Strong'
          else if (suffix.includes('Strong')) level = 'Strong'
          else if (suffix.includes('Moderate')) level = 'Moderate'
          else if (suffix.includes('Supporting')) level = 'Supporting'
        }
      }
      if (code === 'BS1' && level === 'Stand Alone') {
        code = 'BA1'
      }
      
      // 找到对应的criteria
      const criteria = this.allCriteria.find(c => c.code === code)
      if (!criteria) return
      
      if (isMet) {
        // 如果指定了等级且该等级存在，使用该等级
        if (level && criteria.options.some(opt => opt.value === level)) {
          criteria.selected = level
        } else {
          const defaultLevels = {
            PM6: 'Moderate',
            PP1: 'Supporting'
          }
          if (!level && defaultLevels[code] && criteria.options.some(opt => opt.value === defaultLevels[code])) {
            criteria.selected = defaultLevels[code]
            return
          }
          // 没有指定等级或等级不存在，选择最强的选项
          // 强度从高到低排序：Stand Alone > Very Strong > Strong > Moderate > Supporting
          const strengthOrder = ['Stand Alone', 'Very Strong', 'Strong', 'Moderate', 'Supporting']
          for (const strength of strengthOrder) {
            const option = criteria.options.find(opt => opt.value === strength)
            if (option) {
              criteria.selected = strength
              break
            }
          }
        }
      } else {
        // not met，设为Not Applicable
        criteria.selected = 'Not Applicable'
      }
    },
    getCriteriaClass(code) {
      const criteria = this.allCriteria.find(c => c.code === code)
      return criteria ? `criteria-${criteria.type}` : ''
    },
    getCodeClass(code) {
      const criteria = this.allCriteria.find(c => c.code === code)
      return criteria ? `code-${criteria.type}` : ''
    },
    getSummaryRowClass(item) {
      const classes = []
      if (item.type === 'pathogenic') classes.push('summary-row-pathogenic')
      if (item.type === 'benign') classes.push('summary-row-benign')
      if (item.selected !== 'Not Applicable') classes.push('summary-row-selected')
      return classes.join(' ')
    },
    onManualChange(code) {
      this.presetAppliedCodes.delete(code)
    },
    async fetchVariantData() {
      if (!this.variantId) return
      if (!/^\d+$/.test(String(this.variantId))) return
      try {
        const params = new URLSearchParams()
        params.append('ref', this.reference)
        params.append('alt', this.alternate)
        const response = await axios.get(`/api/variant/${this.variantId}?${params.toString()}`)
        this.variantData = response.data
      } catch (error) {
        console.error('Error fetching variant data:', error)
        this.variantData = { reference: [], exact_reference: [], mitotip: [], apogee: [], napogee: [], freq: [] }
      }
    },
    async predict() {
      const data = {}
      this.allCriteria.forEach(item => {
        data[item.code] = item.selected
      })
      
      try {
        const response = await axios.post('/api/predict', data)
        this.result = response.data.result
        this.showResult = true

        // 查询 ClinGen/MITOMAP 匹配
        if (this.combinedAllele) {
          try {
            const lookupResp = await axios.get('/api/lookup', {
              params: { position: this.variantId, ref: this.reference, alt: this.alternate }
            })
            this.lookupData = lookupResp.data
          } catch (e) {
            this.lookupData = { clingen: null, mitomap: null }
          }
        } else {
          this.lookupData = { clingen: null, mitomap: null }
        }
      } catch (error) {
        console.error('Error predicting:', error)
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.page-header {
  text-align: center;
  margin-bottom: 2rem;
}

.page-subtitle {
  margin-top: 1.1rem;
  font-size: 1rem;
  font-weight: 500;
}

.title-link {
  color: #00d4ff;
  text-decoration: none;
}

.title-link:hover {
  text-decoration: underline;
}

.page-title {
  font-size: 1.7rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 10px rgba(0, 212, 255, 0.3);
  margin-bottom: 0.5rem;
}

.variant-input-section {
  background: rgba(30, 50, 80, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
}

.input-row {
  display: flex;
  gap: 2rem;
  justify-content: center;
  align-items: flex-start;
}

.input-group {
  flex: 1;
  max-width: 300px;
}

.input-label {
  font-size: 1.1rem;
  font-weight: 600;
  color: #e0e0e0;
  margin-bottom: 1rem;
  text-align: center;
}

.input-wrapper {
  display: flex;
  gap: 1rem;
  align-items: stretch;
}

.variant-input {
  flex: 1;
  padding: 0 1.2rem;
  border: 2px solid rgba(0, 212, 255, 0.3);
  border-radius: 10px;
  font-size: 1rem;
  background: rgba(15, 25, 45, 0.8);
  color: #fff;
  transition: all 0.3s ease;
  text-align: center;
  height: 42px;
  box-sizing: border-box;
  line-height: 42px;
}

.variant-input::-webkit-outer-spin-button,
.variant-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.variant-input[type=number] {
  -moz-appearance: textfield;
}

.variant-input:focus {
  outline: none;
  border-color: #00d4ff;
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.2);
}

.variant-input::placeholder {
  color: rgba(255, 255, 255, 0.4);
}

.criteria-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.criteria-block {
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  background: rgba(20, 30, 50, 0.4);
}

.block-header {
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.block-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.criteria-card {
  background: rgba(30, 50, 80, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.criteria-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}

.radio-preset {
  background: rgba(255, 179, 0, 0.12) !important;
  border-color: rgba(255, 179, 0, 0.55) !important;
}

.criteria-pathogenic {
  border-left: 4px solid #00d4ff;
}

.criteria-benign {
  border-left: 4px solid #26a69a;
}

.criteria-header {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
}

.criteria-code {
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.85rem;
  color: white;
}

.code-pathogenic {
  background: linear-gradient(135deg, #00d4ff 0%, #7b2cbf 100%);
}

.code-benign {
  background: linear-gradient(135deg, #26a69a 0%, #00bfa5 100%);
}

.criteria-name {
  font-size: 1rem;
  font-weight: 600;
  color: #e0e0e0;
}

.reference-data {
  background: rgba(15, 25, 45, 0.5);
  border-radius: 8px;
  padding: 0.8rem;
  margin-bottom: 0.8rem;
  width: 100%;
  box-sizing: border-box;
  margin-top: 1rem;
}

.reference-title {
  color: #00d4ff;
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.4rem;
}

.reference-item {
  margin-bottom: 0.4rem;
}

.ref-badge {
  display: inline-block;
  background: rgba(0, 212, 255, 0.15);
  border: 1px solid rgba(0, 212, 255, 0.3);
  color: #e0e0e0;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
}

.ref-badge.no-data {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #707070;
}

.reference-link {
  margin-top: 0.4rem;
}

.ext-link {
  color: #00d4ff;
  text-decoration: none;
  font-size: 0.8rem;
  transition: color 0.2s;
}

.ext-link:hover {
  color: #7b2cbf;
}

.scoring-table {
  margin-top: 0.4rem;
}

.score-divider {
  border-top: 1px dashed rgba(0, 212, 255, 0.4);
  margin: 1rem 0;
}

.table-title {
  color: #a0a0a0;
  font-size: 0.8rem;
  margin-bottom: 0.4rem;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.7rem;
  color: #a0a0a0;
}

.data-table th,
.data-table td {
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 0.2rem 0.3rem;
  text-align: left;
}

.data-table th {
  background: rgba(0, 212, 255, 0.1);
  color: #00d4ff;
}

.radio-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.radio-option {
  display: flex;
  align-items: flex-start;
  gap: 0.4rem;
  padding: 0.4rem 0.6rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.radio-option:hover {
  background: rgba(0, 212, 255, 0.08);
}

.radio-option.selected {
  background: rgba(0, 212, 255, 0.15);
  border-color: rgba(0, 212, 255, 0.5);
}

.criteria-benign .radio-option:hover {
  background: rgba(38, 166, 154, 0.08);
}

.criteria-benign .radio-option.selected {
  background: rgba(38, 166, 154, 0.15);
  border-color: rgba(38, 166, 154, 0.5);
}

.criteria-benign .reference-title {
  color: #26a69a;
}

.criteria-benign .ref-badge {
  background: rgba(38, 166, 154, 0.15);
  border-color: rgba(38, 166, 154, 0.3);
}

.criteria-benign .score-divider {
  border-color: rgba(38, 166, 154, 0.4);
}

.criteria-benign .data-table th {
  background: rgba(38, 166, 154, 0.1);
  color: #26a69a;
}

.criteria-benign .ext-link:hover {
  color: #00bfa5;
}

.radio-input {
  margin-top: 0.15rem;
  accent-color: #00d4ff;
}

.criteria-benign .radio-input {
  accent-color: #26a69a;
}

.radio-label {
  font-weight: 500;
  color: #e0e0e0;
  font-size: 0.9rem;
  position: relative;
}

.asterisk {
  color: #f44336;
  font-weight: 700;
  font-size: 0.8rem;
  position: absolute;
  top: -4px;
  right: -8px;
}

.radio-caption {
  display: block;
  color: #707070;
  font-size: 0.75rem;
  margin-top: 0.15rem;
  margin-left: 1.3rem;
}

.action-section {
  margin-top: 2rem;
  text-align: center;
}

.predict-btn {
  padding: 1rem 3rem;
  background: linear-gradient(135deg, #00d4ff 0%, #7b2cbf 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  box-shadow: 0 5px 20px rgba(0, 212, 255, 0.4);
}

.predict-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 30px rgba(0, 212, 255, 0.5);
}

.btn-icon {
  font-size: 1.3rem;
}

.citation-footer {
  margin-top: 2rem;
  text-align: center;
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.citation-text {
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.8rem;
  line-height: 1.6;
  font-style: italic;
}

.citation-link {
  color: rgba(0, 212, 255, 0.7);
  text-decoration: none;
  transition: color 0.2s ease;
}

.citation-link:hover {
  color: #00d4ff;
  text-decoration: underline;
}

.result-section {
  margin-top: 2rem;
  text-align: center;
  animation: slideUp 0.5s ease;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

.result-card {
  width: 100%;
  padding: 2rem 4rem;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  font-family: 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.result-header {
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.15rem;
  font-weight: 500;
}

.result-variant {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.2rem;
  letter-spacing: 0.05em;
  font-weight: 600;
}

.result-label {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.result-pathogenic {
  background: linear-gradient(135deg, #f44336 0%, #c62828 100%);
  border: 2px solid rgba(244, 67, 54, 0.5);
}

.result-likely-pathogenic {
  background: linear-gradient(135deg, #ff9800 0%, #ef6c00 100%);
  border: 2px solid rgba(255, 152, 0, 0.5);
}

.result-benign {
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  border: 2px solid rgba(76, 175, 80, 0.5);
}

.result-likely-benign {
  background: linear-gradient(135deg, #8bc34a 0%, #558b2f 100%);
  border: 2px solid rgba(139, 195, 74, 0.5);
}

.result-vus {
  background: linear-gradient(135deg, #607d8b 0%, #455a64 100%);
  border: 2px solid rgba(96, 125, 139, 0.5);
}

.result-value {
  color: white;
  font-size: 3rem;
  font-weight: 700;
}

.result-desc {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  margin-top: 0.5rem;
}

.result-body {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  padding: 0.5rem 0;
}

.result-body.has-lookup {
  justify-content: center;
}

.result-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
}

.result-divider {
  width: 1px;
  height: 60px;
  background: rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
}

.result-lookup {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 1;
  padding-left: 1rem;
}

.lookup-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.3rem;
}

.lookup-source {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  font-weight: 500;
}

.lookup-status {
  color: white;
  font-size: 1.8rem;
  font-weight: 700;
}

.result-footnote {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.75rem;
  text-align: center;
  margin-top: 1rem;
}

.result-override-note {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.7rem;
  font-style: italic;
  margin-top: 0.1rem;
  text-align: center;
  font-weight: 400;
}

.info-card {
  margin-top: 2rem;
  background: rgba(30, 50, 80, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 16px;
  padding: 1rem 2rem 1.5rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.info-card-title {
  color: #00d4ff;
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.info-card p {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.8rem;
  line-height: 1.8;
  margin-bottom: 0.6rem;
  text-align: justify;
  text-justify: inter-word;
}

.info-card p:last-child {
  margin-bottom: 0;
}

.selection-summary {
  margin-top: 2rem;
  background: rgba(30, 50, 80, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
}

.summary-title {
  color: #00d4ff;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: center;
}

.selection-tables-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.selection-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.selection-table thead th {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
  padding: 0.75rem;
  text-align: center;
  font-weight: 600;
  border-bottom: 2px solid rgba(0, 212, 255, 0.3);
}

.selection-table tbody td {
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  text-align: center;
}

.summary-code {
  font-weight: 600;
  color: #fff;
}

.summary-row-pathogenic {
  background: rgba(0, 212, 255, 0.05);
}

.summary-row-benign {
  background: rgba(38, 166, 154, 0.05);
}

.summary-row-selected {
  background: rgba(255, 255, 255, 0.08) !important;
  font-weight: 500;
}

.summary-row-selected .summary-code {
  color: #00d4ff;
}

.summary-preset td {
  background: rgba(255, 179, 0, 0.1) !important;
  border-bottom: 1px solid rgba(255, 179, 0, 0.3) !important;
}

.summary-preset .summary-code {
  color: #ffb300 !important;
}

.preset-disclaimer {
  margin-top: 1rem;
  padding: 0.8rem 1rem;
  background: rgba(255, 179, 0, 0.06);
  border: 1px solid rgba(255, 179, 0, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  line-height: 1.6;
}

.preset-link {
  color: #ffb300;
  text-decoration: none;
  margin-left: 0.3rem;
  transition: color 0.2s;
}

.preset-link:hover {
  color: #ffc940;
  text-decoration: underline;
}

@media (max-width: 900px) {
  .criteria-container {
    grid-template-columns: 1fr;
  }
  
  .input-row {
    flex-direction: column;
    align-items: center;
  }
  
  .input-group {
    width: 100%;
    max-width: none;
  }
  
  .input-wrapper {
    flex-direction: column;
  }
  
  .page-title {
    font-size: 1.5rem;
  }
  
  .selection-tables-container {
    grid-template-columns: 1fr;
  }
  
  .selection-table {
    font-size: 0.8rem;
  }
  
  .selection-table thead th,
  .selection-table tbody td {
    padding: 0.5rem;
  }
}
</style>
